import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class CustomToastService {
  private readonly COLORS = {
    bg: '#FFEECC',
    navy: '#0C1837',
    gold: '#A4800E'
  };

  constructor(private router: Router) {}

  // navigateTo: any valid Angular router path (e.g. '/pages/visa-requests/123' or '/pages/tasks-board')
  // onAction: called on BOTH navigate-click and dismiss — use to mark notification as read
  show(title: string, message: string, navigateTo?: string, onAction?: () => void): void {
    // Remove any existing toasts before showing a new one to prevent stacking
    document.querySelectorAll('[data-custom-toast]').forEach((el) => el.remove());

    const toast = document.createElement('div');
    toast.setAttribute('data-custom-toast', '1');

    Object.assign(toast.style, {
      position: 'fixed',
      top: '24px',
      right: '24px',
      zIndex: '100000',
      background: this.COLORS.bg,
      borderLeft: `4px solid ${this.COLORS.gold}`,
      borderRadius: '8px',
      padding: '14px 44px 14px 18px',
      minWidth: '300px',
      maxWidth: '400px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      cursor: navigateTo ? 'pointer' : 'default',
      fontFamily: 'inherit',
      opacity: '0',
      transition: 'opacity 0.25s ease'
    });

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      color: this.COLORS.navy,
      fontWeight: '700',
      fontSize: '13px',
      marginBottom: '4px',
      letterSpacing: '0.02em'
    });
    titleEl.textContent = title;

    const bodyEl = document.createElement('div');
    Object.assign(bodyEl.style, {
      color: this.COLORS.navy,
      fontSize: '13px',
      lineHeight: '1.5'
    });
    bodyEl.textContent = message;

    toast.appendChild(titleEl);
    toast.appendChild(bodyEl);

    if (navigateTo) {
      const hint = document.createElement('div');
      Object.assign(hint.style, {
        color: this.COLORS.gold,
        fontSize: '11px',
        marginTop: '6px'
      });
      hint.textContent = 'Click to view →';
      toast.appendChild(hint);
    }

    const dismissBtn = document.createElement('button');
    dismissBtn.setAttribute('type', 'button');
    dismissBtn.textContent = '✕';
    Object.assign(dismissBtn.style, {
      position: 'absolute',
      top: '8px',
      right: '8px',
      background: 'none',
      border: 'none',
      color: this.COLORS.navy,
      fontSize: '14px',
      cursor: 'pointer',
      padding: '8px 10px',
      lineHeight: '1',
      opacity: '0.6',
      zIndex: '1'
    });
    dismissBtn.addEventListener('mouseenter', () => {
      dismissBtn.style.opacity = '1';
    });
    dismissBtn.addEventListener('mouseleave', () => {
      dismissBtn.style.opacity = '0.6';
    });
    toast.appendChild(dismissBtn);

    document.body.appendChild(toast);

    // Fade in — double rAF guarantees initial opacity:0 is committed before transitioning to 1
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
      });
    });

    const remove = () => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(toast)) toast.remove();
      }, 250);
    };

    // X button: direct handler so it always fires reliably on all devices
    dismissBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (onAction) onAction();
      remove();
    });

    // Toast body click: mark as read, remove, and navigate
    toast.addEventListener('click', () => {
      if (onAction) onAction();
      remove();
      if (navigateTo) this.router.navigateByUrl(navigateTo);
    });
  }
}
